import { Box, IconButton, Toolbar, Typography, useTheme } from "@mui/material";
import { ArrowBack, Campaign } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  Font,
  Image,
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { PDFViewer } from "@react-pdf/renderer";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getNewsletterById } from "apis";
import { CircularProgress } from "@mui/material";

Font.register({ family: "FamilyName", src: "/NotoKufiArabic-Bold.ttf" });

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
    direction: "rtl",
  },
  viewer: {
    width: "100%",
    height: "calc(100vh - 64px)",
    border: "none",
  },
});

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toMonthName = (month: string) => {
  const idx = parseInt(month, 10) - 1;
  return MONTH_NAMES[idx] || month;
};

interface NewsletterItem {
  year: string;
  month: string;
  contentResume: string;
  contentMovement: string;
  contentBirthday: string;
  contentEvent: string;
  contentMessage: string;
  images: string[];
}

const ViewNewsletter: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stateItem = location.state as NewsletterItem | null;
  const [item, setItem] = useState<NewsletterItem | null>(stateItem);
  const [loading, setLoading] = useState(!stateItem);

  const id = searchParams.get("id");

  useEffect(() => {
    if (stateItem || !id) return;
    setLoading(true);
    getNewsletterById(id)
      .then((data) => setItem(data))
      .catch(() => navigate("/newsletter", { replace: true }))
      .finally(() => setLoading(false));
  }, [id, stateItem, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    navigate("/newsletter");
    return null;
  }

  const monthName = toMonthName(item.month);
  const year = dayjs(item.year).format("YYYY");

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/newsletter")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Campaign sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t(`labels.months_name.${monthName}`)} {year}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("newsletter.label")}
        </Typography>
      </Toolbar>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <PDFViewer style={styles.viewer}>
          <Document>
            <Page size="A4" style={styles.page}>
              <View
                style={{
                  color: "white",
                  textAlign: "center",
                  margin: 30,
                  flexDirection: "row",
                }}
              >
                <View>
                  <Image
                    style={{ width: 60, height: 60, padding: 10 }}
                    src="/logo.jpg"
                  />
                </View>
                <View>
                  <Text style={{ color: "#000", marginTop: 20 }}>
                    eParentSpace
                  </Text>
                </View>
              </View>
              <View
                style={{
                  textAlign: "center",
                  fontSize: 40,
                  fontFamily: "FamilyName",
                }}
              >
                <Text>{t("newsletter.label")}</Text>
                <Text style={{ fontSize: 24 }}>
                  {t(`labels.months_name.${monthName}`)}/{year}
                </Text>
              </View>
              <View
                style={{
                  border: "3px solid brown",
                  marginHorizontal: 30,
                  height: 520,
                  marginTop: 20,
                }}
              >
                <Text
                  style={{
                    paddingVertical: 20,
                    textAlign: "center",
                    fontWeight: "ultrabold",
                    fontSize: 24,
                    fontFamily: "FamilyName",
                  }}
                >
                  {t("labels.activity_summary")}
                </Text>
                <Text style={{ marginHorizontal: 20, fontSize: 14 }}>
                  {item.contentResume}
                </Text>
                <View>
                  {item.images &&
                    item.images.length > 0 &&
                    item.images.map((image, idx) => (
                      <Image
                        key={idx}
                        src={image}
                        style={{ height: 160, padding: 10 }}
                      />
                    ))}
                </View>
              </View>
            </Page>
            <Page size="A4" style={styles.page}>
              <View style={{ flexDirection: "row", marginTop: 10 }}>
                <View
                  style={{
                    border: "3px solid brown",
                    marginLeft: 30,
                    height: 360,
                    marginTop: 20,
                    width: "48%",
                  }}
                >
                  <Text
                    style={{
                      paddingVertical: 20,
                      textAlign: "center",
                      fontWeight: "ultrabold",
                      fontSize: 20,
                      fontFamily: "FamilyName",
                    }}
                  >
                    {t("inputs.mfriends")}
                  </Text>
                  <Text style={{ marginHorizontal: 20, fontSize: 14 }}>
                    {item.contentMovement}
                  </Text>
                </View>
                <View
                  style={{
                    border: "3px solid brown",
                    marginHorizontal: 30,
                    height: 360,
                    marginTop: 20,
                    width: "48%",
                  }}
                >
                  <Text
                    style={{
                      paddingVertical: 20,
                      textAlign: "center",
                      fontWeight: "ultrabold",
                      fontSize: 20,
                      fontFamily: "FamilyName",
                    }}
                  >
                    {t("inputs.birthdays")}
                  </Text>
                  <Text style={{ marginHorizontal: 20, fontSize: 14 }}>
                    {item.contentBirthday}
                  </Text>
                </View>
              </View>
              <View style={{ flexDirection: "row" }}>
                <View
                  style={{
                    border: "3px solid brown",
                    marginLeft: 30,
                    height: 400,
                    marginTop: 20,
                    width: "48%",
                  }}
                >
                  <Text
                    style={{
                      paddingVertical: 20,
                      textAlign: "center",
                      fontWeight: "ultrabold",
                      fontSize: 20,
                      fontFamily: "FamilyName",
                    }}
                  >
                    {t("labels.upcoming_events")}
                  </Text>
                  <Text style={{ marginHorizontal: 20, fontSize: 14 }}>
                    {item.contentEvent}
                  </Text>
                </View>
                <View
                  style={{
                    border: "3px solid brown",
                    marginHorizontal: 30,
                    height: 400,
                    marginTop: 20,
                    width: "48%",
                  }}
                >
                  <Text
                    style={{
                      paddingVertical: 20,
                      textAlign: "center",
                      fontWeight: "ultrabold",
                      fontSize: 20,
                      fontFamily: "FamilyName",
                    }}
                  >
                    {t("labels.messages")}
                  </Text>
                  <Text style={{ marginHorizontal: 20, fontSize: 14 }}>
                    {item.contentMessage}
                  </Text>
                </View>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      </Box>
    </>
  );
};

export default ViewNewsletter;
